import { ReactNode } from 'react';
import { ReservationLinkResult } from '@mews-commander/data-access';
import { FeatureFlag } from '@mews-commander/enums';
import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import { Amount, WithAccountId } from '@mews-commander/types';
import { ConvertedCurrencyValue } from '@mews-commander/ui';
import { sumAllAmounts } from '@mews-commander/utils';
import { lt, useFeatureFlag, useToggle } from '@mews/framework';
import { Accordion, Card, Container, Divider, Stack, Typography } from '@optimus-web/core';
import { useGetAmountsForPaymentPlanSuspense } from './hooks';
import { MonthlyPaymentPlanAmount } from './types';

const PaymentPlanPaymentDetailsAccordionBody = ({
    monthName,
    amount,
}: Omit<MonthlyPaymentPlanAmount, 'monthIndex'>) => (
    <Stack vertical spacing="100">
        <Stack distribution="space-between">
            <Typography textStyle="bodyLarge" as="div">
                {monthName}
            </Typography>
            <Typography textStyle="bodyMediumStrong" as="div">
                <ConvertedCurrencyValue amount={amount} />
            </Typography>
        </Stack>
    </Stack>
);

const PaymentPlanPaymentDetailsAccordionWrapper = ({ children }: { children: ReactNode }) => {
    const isSteppedCreatePaymentPlanFormEnabled = useFeatureFlag(FeatureFlag.steppedCreatePaymentPlanFormEnabled);

    return isSteppedCreatePaymentPlanFormEnabled ? (
        <Card padding="200">{children}</Card>
    ) : (
        <Stack vertical spacing="100">
            {children}
            <Divider />
        </Stack>
    );
};

export const PaymentPlanPaymentDetailsAccordion = ({
    reservation,
}: { reservation: ReservationLinkResult } & WithAccountId) => {
    const [expanded, toggleExpanded] = useToggle();
    const { monthlyAmounts } = useGetAmountsForPaymentPlanSuspense(reservation);
    const enterprise = useCurrentEnterprise();

    return (
        <PaymentPlanPaymentDetailsAccordionWrapper>
            <Accordion
                expanded={expanded}
                onClick={toggleExpanded}
                headerPadding={{ top: '0', bottom: '0', left: '0', right: '0' }}
                title={
                    <Typography textStyle="bodyLargeStrong" as="div">
                        {lt('PaymentDetailPlural')}
                    </Typography>
                }
                endSlot={
                    <Typography textStyle="bodyLarge" as="div">
                        <ConvertedCurrencyValue
                            amount={sumAllAmounts(
                                monthlyAmounts.map(({ amount }) => amount as Amount),
                                enterprise.defaultCurrencyCode
                            )}
                        />
                    </Typography>
                }
                body={
                    <Container padding={['100', '0']}>
                        <Stack vertical spacing="100">
                            {monthlyAmounts.map(({ monthName, amount, monthIndex }) => (
                                <PaymentPlanPaymentDetailsAccordionBody
                                    monthName={monthName}
                                    amount={amount}
                                    key={monthIndex}
                                />
                            ))}
                        </Stack>
                    </Container>
                }
                bodyPadding="0"
                withHeaderBorder={false}
                withBodyBorder={false}
            />
        </PaymentPlanPaymentDetailsAccordionWrapper>
    );
};
